import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Leaderboard from '../components/Leaderboard';
import { getTierBadge } from '../theme/tier';
import { colors, radii, spacing } from '../theme/tokens';
import { Card, Icon, BrandMark } from '../components/ui';

const Home = () => {
  const { user, isSilver, isGold, isPlatinum, canAddData, canApprove } = useAuth();
  const navigate = useNavigate();
  const badge = getTierBadge(user?.tier);

  const tierLabel = isPlatinum ? 'Platinum Tier' : isGold ? 'Gold Tier' : 'Silver Tier';

  return (
    <div style={styles.container}>
      <div style={styles.hero}>
        <BrandMark size={84} />
        <div>
          <h1 style={styles.title}>Welcome to PAR Car Parts Manager</h1>
          <p style={styles.subtitle}>Professional Car Parts Inventory System</p>
        </div>
      </div>

      {user && (
        <div style={styles.userCard}>
          <div style={styles.userCardHead}>
            <h2 style={styles.sectionTitle}>Your account status</h2>
            <span style={{ ...styles.tierPill, background: badge.color }}>
              <Icon name={badge.icon} size={16} color="#1a1a1a" /> {tierLabel}
            </span>
          </div>
          <div style={styles.tierInfo}>
            <div style={styles.tokens}>
              <Icon name="paid" size={20} color={colors.parGreenBright} />
              <span style={{ fontWeight: 800, fontSize: 20 }}>{user.tokens}</span>
              <span style={{ color: colors.textMuted }}>tokens</span>
            </div>
          </div>

          {isSilver && (
            <div style={styles.limitInfo}>
              <p style={{ margin: '0 0 4px' }}>Daily searches: {user.dailySearchCount} / 5</p>
              <p style={{ margin: '0 0 4px' }}>Remaining: {5 - user.dailySearchCount} searches today</p>
              <p style={styles.upgradeNote}>
                <Icon name="upgrade" size={16} color={colors.parGreenBright} /> Upgrade to Gold for unlimited searches and the ability to add parts.
              </p>
            </div>
          )}
        </div>
      )}

      <div style={styles.features}>
        <Card
          title="Find Car Parts"
          titleIcon="search"
          optional={isSilver ? 'Limited: 5 / day' : 'Unlimited'}
          actionIcon="arrow_forward"
          onClick={() => navigate('/car-parts/find')}
          onAction={() => navigate('/car-parts/find')}
        >
          Search our extensive database of car parts by make, model and variant.
        </Card>

        {canAddData && (
          <Card
            title="Add Car Parts"
            titleIcon="add_circle"
            optional="Gold+ feature"
            onClick={() => navigate('/car-parts/add')}
            onAction={() => navigate('/car-parts/add')}
          >
            Contribute new parts to the shared database and earn tokens.
          </Card>
        )}

        {canApprove && (
          <Card
            title="Admin Dashboard"
            titleIcon="diamond"
            optional="Platinum"
            onClick={() => navigate('/admin')}
            onAction={() => navigate('/admin')}
          >
            Approve submissions, manage users and award tokens.
          </Card>
        )}

        {isSilver && (
          <Card
            title="Upgrade Available"
            titleIcon="rocket_launch"
            optional="Unlock more"
            action={false}
          >
            Contact an admin for Gold or Platinum access to unlock more features.
          </Card>
        )}
      </div>

      <Leaderboard />

      <div style={styles.tierComparison}>
        <h2 style={styles.comparisonTitle}>Membership tiers</h2>
        <div style={styles.tiersGrid}>
          <TierCard icon="workspace_premium" name="Silver" price="Free" items={[
            ['check', 'Search up to 5 parts/day'],
            ['check', 'View approved data'],
            ['close', 'Cannot add parts'],
            ['close', 'No admin access']
          ]} />
          <TierCard icon="military_tech" name="Gold" price="Premium" highlight items={[
            ['check', 'Unlimited searches'],
            ['check', 'Add new car parts'],
            ['check', 'Earn tokens for contributions'],
            ['close', 'No admin access']
          ]} />
          <TierCard icon="diamond" name="Platinum" price="Admin" highlightStrong items={[
            ['check', 'All Gold features'],
            ['check', 'Approve submissions'],
            ['check', 'Manage users'],
            ['check', 'Award tokens']
          ]} />
        </div>
      </div>
    </div>
  );
};

const TierCard = ({ icon, name, price, items, highlight, highlightStrong }) => (
  <div
    style={{
      ...styles.tierCard,
      border: `1px solid ${highlightStrong ? colors.parGreen : highlight ? colors.parLime : colors.glassBorder}`,
      transform: highlight ? 'scale(1.03)' : 'none'
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <Icon name={icon} size={26} color={colors.parGreenBright} />
      <h3 style={{ margin: 0, color: colors.text, fontSize: 20 }}>{name}</h3>
    </div>
    <div style={styles.price}>{price}</div>
    <ul style={styles.featureList}>
      {items.map(([ic, text], i) => (
        <li key={i} style={styles.featureItem}>
          <Icon name={ic} size={18} color={ic === 'check' ? colors.parGreenBright : colors.textFaint} />
          <span style={{ color: ic === 'check' ? colors.text : colors.textFaint }}>{text}</span>
        </li>
      ))}
    </ul>
  </div>
);

const styles = {
  container: { maxWidth: 1200, margin: '0 auto', padding: '24px 20px 40px' },
  hero: {
    display: 'flex', alignItems: 'center', gap: 22, flexWrap: 'wrap',
    padding: '28px', background: colors.glass, border: `1px solid ${colors.glassBorder}`,
    borderRadius: radii.lg, marginBottom: spacing.xl, backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)'
  },
  title: { fontSize: 'clamp(24px, 4vw, 36px)', margin: '0 0 6px 0', color: colors.text, fontWeight: 800 },
  subtitle: { fontSize: 16, margin: 0, color: colors.textMuted },
  userCard: {
    background: colors.glass, border: `1px solid ${colors.glassBorder}`, padding: 24,
    borderRadius: radii.lg, marginBottom: spacing.xl, backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)'
  },
  userCardHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' },
  sectionTitle: { margin: 0, fontSize: 20, color: colors.text },
  tierPill: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 800, color: '#1a1a1a', padding: '6px 12px', borderRadius: radii.pill },
  tierInfo: { display: 'flex', alignItems: 'center', gap: 16, marginTop: 14 },
  tokens: { display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(47,182,92,0.10)', padding: '8px 16px', borderRadius: radii.pill, color: colors.text },
  limitInfo: { marginTop: 18, padding: 16, background: 'rgba(145,200,62,0.08)', borderRadius: radii.md, borderLeft: `4px solid ${colors.parLime}`, color: colors.textMuted, fontSize: 14 },
  upgradeNote: { display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, color: colors.parGreenBright, marginTop: 10, marginBottom: 0 },
  features: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 18, marginBottom: spacing.xxl },
  tierComparison: { marginTop: 48 },
  comparisonTitle: { textAlign: 'center', marginBottom: 26, color: colors.text },
  tiersGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 18 },
  tierCard: {
    background: colors.glass, padding: 26, borderRadius: radii.lg, color: colors.text,
    backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)', transition: 'transform 0.2s ease'
  },
  price: { fontSize: 22, fontWeight: 800, color: colors.parGreenBright, margin: '14px 0' },
  featureList: { listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 10 },
  featureItem: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }
};

export default Home;
